import { getConversationPersistenceService, getRuntimeGlobalState, } from "./conversationRuntimeService";
function getRuntimeDependencies() {
    return {
        globalState: getRuntimeGlobalState(),
        persistence: getConversationPersistenceService(),
    };
}
export async function fetchConversations(options) {
    const { origin = "mainTaskpane", primaryAgentType, resolveFallbackTitle } = options;
    if (!primaryAgentType) {
        throw new Error("fetchConversations requires primaryAgentType.");
    }
    try {
        const { globalState, persistence } = getRuntimeDependencies();
        const persistedMetadata = await persistence.listConversations({ origin });
        const persistedTitles = new Map(persistedMetadata.map((meta) => [String(meta.conversationId), String(meta.title || "")]));
        const conversations = Object.values(globalState.conversationRegistry || {});
        const inMemoryConversations = conversations
            .filter((conversation) => conversation.conversationOrigin === origin)
            .map((conversation) => {
            const threads = conversation.agentRegistry.threadManager.getThreadsForConversation(conversation.id);
            let messageCount = 0;
            let lastActiveAt = new Date().toISOString();
            let hasUserMessages = false;
            let hasAssistantMessages = false;
            threads.forEach((thread) => {
                messageCount += thread.messages.length;
                if (thread.lastActivityAt > new Date(lastActiveAt).getTime()) {
                    lastActiveAt = new Date(thread.lastActivityAt).toISOString();
                }
                thread.messages.forEach((message) => {
                    if (message.role === "user")
                        hasUserMessages = true;
                    if (message.role === "assistant")
                        hasAssistantMessages = true;
                });
            });
            let title = persistedTitles.get(conversation.id) || "";
            if (!title) {
                const primaryThread = threads.find((thread) => thread.agentType === primaryAgentType);
                const firstUserMessage = primaryThread?.messages?.find((message) => message.role === "user");
                if (firstUserMessage && typeof firstUserMessage.content === "string") {
                    title = firstUserMessage.content.substring(0, 50).trim().replace(/\s+/g, " ");
                    if (firstUserMessage.content.length > 50) {
                        title += "...";
                    }
                }
            }
            if (!title) {
                const fallbackTitle = resolveFallbackTitle?.(conversation);
                title =
                    typeof fallbackTitle === "string" && fallbackTitle.trim().length > 0
                        ? fallbackTitle
                        : `Conversation ${conversation.id.substring(0, 8)}`;
            }
            const hasMeaningfulTitle = !title.startsWith("Conversation ");
            const hasMeaningfulMessages = hasUserMessages && hasAssistantMessages;
            return {
                conversationId: conversation.id,
                title,
                messageCount,
                lastActiveAt,
                status: "active",
                isInMemory: true,
                _isMeaningful: hasMeaningfulTitle || hasMeaningfulMessages,
            };
        })
            .filter((conversation) => conversation._isMeaningful)
            .map(({ _isMeaningful, ...conversation }) => conversation);
        const inMemoryIds = new Set(inMemoryConversations.map((conversation) => conversation.conversationId));
        const persistedConversations = persistedMetadata
            .filter((meta) => !inMemoryIds.has(meta.conversationId))
            .filter((meta) => {
            const title = typeof meta.title === "string" ? meta.title : "";
            const messageCount = typeof meta.messageCount === "number" ? meta.messageCount : 0;
            return !title.startsWith("Conversation ") || messageCount >= 2;
        })
            .map((meta) => ({
            conversationId: meta.conversationId,
            title: meta.title,
            messageCount: meta.messageCount,
            lastActiveAt: meta.lastActiveAt,
            status: meta.status,
            isInMemory: false,
        }));
        return [...inMemoryConversations, ...persistedConversations].sort((a, b) => new Date(b.lastActiveAt).getTime() - new Date(a.lastActiveAt).getTime());
    }
    catch (error) {
        console.error("[ConversationFetch] Error fetching conversations:", error);
        return [];
    }
}
